import { Link } from 'react-router-dom';
import { type JournalEntry } from '../../services/journalService';
import { Button } from '../atoms/Button/Button';
import { Badge } from '../atoms/Badge/Badge';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { useTranslation } from '../../i18n/useTranslation';
import {
  EntryPhotoPreview,
  ENTRY_PHOTO_BODY_CLASS,
  ENTRY_PHOTO_HOST_CLASS,
  entryPhotoSrc,
} from '../media/EntryPhotoPreview/EntryPhotoPreview';
import { formatJournalDate, journalTagLabel } from './journalTags';
import './JournalEntryCard.css';

export interface JournalEntryCardProps {
  entry: JournalEntry;
  onEdit: (entry: JournalEntry) => void;
  onDelete: (entry: JournalEntry) => void;
}

function getPreviewText(content: string, maxLength = 150): string {
  const text = content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trimEnd() + '…';
}

/**
 * One journal entry in the overview list: optional cover photo on the left
 * (stacked on top on narrow screens; the shared EntryPhotoPreview, also used
 * by the community feed), then date, visibility, title, excerpt, tags and the
 * edit/delete actions.
 */
export function JournalEntryCard({ entry, onEdit, onDelete }: JournalEntryCardProps) {
  const { t, i18n } = useTranslation();
  const cover = entry.coverImage ?? null;
  const hasCover = entryPhotoSrc(cover) !== null;
  const isPrivate = entry.visibility !== 'public';
  const tags = entry.tags ?? [];

  return (
    <article
      className={`journal-entry-card${hasCover ? ` journal-entry-card--with-cover ${ENTRY_PHOTO_HOST_CLASS}` : ''}`}
      data-testid="journal-entry-card"
    >
      {cover && hasCover && <EntryPhotoPreview image={cover} data-testid="journal-entry-card-cover" />}

      <div className={`journal-entry-card__body ${ENTRY_PHOTO_BODY_CLASS}`}>
        <div className="journal-entry-card__header">
          <div className="journal-entry-card__meta">
            <time className="journal-entry-card__date" dateTime={entry.createdAt}>
              {formatJournalDate(entry.createdAt, i18n.language)}
            </time>
            <Badge tone="neutral" className="journal-entry-card__visibility">
              <span aria-hidden="true">{isPrivate ? '🔒' : '🌐'}</span>{' '}
              {isPrivate ? t('journal.card.visibilityPrivate') : t('journal.card.visibilityPublic')}
            </Badge>
            {entry.source === 'voice' && <Badge tone="info">{t('voice.source.voice')}</Badge>}
          </div>
          <div className="journal-entry-card__actions">
            <Button
              variant="secondary"
              size="sm"
              aria-label={t('journal.card.editLabel', { title: entry.title })}
              onClick={() => onEdit(entry)}
            >
              {t('journal.card.edit')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="journal-entry-card__delete"
              aria-label={t('journal.card.deleteLabel', { title: entry.title })}
              onClick={() => onDelete(entry)}
            >
              {t('journal.card.delete')}
            </Button>
          </div>
        </div>

        <Heading level={2} className="journal-entry-card__title">
          <Link
            className="journal-entry-card__title-link"
            to={`/aquariums/${entry.aquariumId}/journal/${entry.id}`}
          >
            {entry.title}
          </Link>
        </Heading>

        {entry.content && (
          <Text className="journal-entry-card__preview">{getPreviewText(entry.content)}</Text>
        )}

        {tags.length > 0 && (
          <ul className="journal-entry-card__tags" aria-label={t('journal.tags')}>
            {tags.map((tag) => (
              <li key={tag} className="journal-entry-card__tag">
                {journalTagLabel(tag, t)}
              </li>
            ))}
          </ul>
        )}

        {entry.updatedAt !== entry.createdAt && (
          <Text className="journal-entry-card__updated">
            <time dateTime={entry.updatedAt}>
              {t('journal.card.updated', { date: formatJournalDate(entry.updatedAt, i18n.language) })}
            </time>
          </Text>
        )}
      </div>
    </article>
  );
}
